import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import styles from "./TaskEditorSheet.module.css";
import CreatorFragment, {
  CreatorFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import Section from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Section/Section";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import Header from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Header/Header";
import {
  PatchTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { NotFound } from "@coworker/apprestructured/src/task-management/components/NotFound/NotFound";
import { useTranslation } from "@coworker/locales";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import CreateTaskSchedule, {
  CreateTaskScheduleChangeEvent,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/CreateTaskSchedule/CreateTaskSchedule";

/**
 * The TaskEditorSheet is open for the task with id 'taskEditor' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskEditor'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskEditorSheet() {
  const [{ taskEditor: taskId }, setSearchParams] = useSearchParamState();
  const [originalTask, setOriginalTask] = useState<Task | null>(null);
  const [patchTask, setPatchTask] = useState<PatchTask>({});

  const { t } = useTranslation();

  const { mutateAsync: updateTask, isPending: isSaving } = useUpdateTask();
  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: fetchedOriginalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);

  const handleFetchedOriginalTaskChange = useEffectEvent(
    (fetchedOriginalTask: Task | null | undefined) => {
      // This is in order to show a populated sheet while closing the modal:
      if (fetchedOriginalTask) {
        setOriginalTask(fetchedOriginalTask);
        setPatchTask({});
        // The modal is open but no task is found:
      } else if (taskId) {
        setOriginalTask(null);
      }
    }
  );

  useEffect(() => {
    handleFetchedOriginalTaskChange(fetchedOriginalTask);
  }, [fetchedOriginalTask]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return null;
    }
    return (
      allTaskTypes.find(({ id }) => id === originalTask.task_type_id) || null
    );
  }, [originalTask, allTaskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const handleSaveClick = useCallback(() => {
    async function fn() {
      if (taskId) {
        // We can skip saving if no changes are made
        if (Object.keys(patchTask).length > 0) {
          const updatedTask = await updateTask({
            id: taskId,
            patchTask,
          });
          setOriginalTask(updatedTask);
        }
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", taskId);
        });
      }
    }
    fn();
  }, [taskId, updateTask, patchTask, disableSaveGuard, setSearchParams]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskEditor");
    });
  }, [setSearchParams]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const header = useMemo(() => {
    if (loadingFailed) {
      return <FixaModalHeader closeBtnClick={handleCloseClick} />;
    } else {
      return (
        <FixaModalHeader
          closeBtnClick={handleCloseClick}
          title={t("editTaskString")}
        />
      );
    }
  }, [handleCloseClick, loadingFailed, t]);

  const footer = useMemo(() => {
    if (loadingFailed) {
      return null;
    } else {
      return (
        <FixaModalFooter>
          <FixaButton
            onClick={handleSaveClick}
            text={t("saveTaskString")}
            fluid
            type="primary"
            loading={isSaving}
            disabled={!taskType || !originalTask || !taskId}
            htmlType="button"
          />
        </FixaModalFooter>
      );
    }
  }, [
    loadingFailed,
    handleSaveClick,
    t,
    isSaving,
    taskType,
    originalTask,
    taskId,
  ]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      setPatchTask((prev) => merge(prev, part));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const blockInput = useMemo(() => {
    return isSaving || isFetchingOriginalTask || !taskId;
  }, [isFetchingOriginalTask, isSaving, taskId]);

  const mergedTask = useMemo(() => {
    return originalTask ? merge(originalTask, patchTask) : LOADING;
  }, [originalTask, patchTask]);

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput: blockInput,
      task: mergedTask,
      onTaskChange: handleTaskChange,
      showValidation: false,
    };
  }, [blockInput, mergedTask, handleTaskChange]);

  const handleScheduleChange = useCallback(
    ({ futureStart }: CreateTaskScheduleChangeEvent) => {
      handleTaskChange({ start: futureStart });
      // Ignore other values for now
    },
    [handleTaskChange]
  );

  const futureStart = useMemo(() => {
    if (isLoading(mergedTask) || mergedTask["start"] === null) {
      return "";
    } else {
      return mergedTask["start"] as string;
    }
  }, [mergedTask]);

  const content = useMemo(() => {
    if (loadingFailed) {
      return (
        <NotFound
          title={t("sorryTaskCouldNotBeFoundString")}
          message={t("thisTaskDoesNotExistString")}
        />
      );
    } else {
      return (
        <div className={styles["sheetsContent"]}>
          <Section>
            <Header number="1" label={t("taskDetailsString")} />
            <FixaListView id="details">
              {taskType ? (
                taskType.creator_fragments.map((config, i) => {
                  return (
                    <CreatorFragment
                      key={i}
                      config={config}
                      commonProps={creatorFragmentCommonProps}
                    />
                  );
                })
              ) : (
                <LoadingCreatorFragment />
              )}
            </FixaListView>
          </Section>
          <Section>
            <Header number="2" label={t("taskSettingsString")} />
            <FixaListView id="settings">
              <CreatorFragment
                config={{ id: "assign-to-creator-fragment", props: {} }}
                commonProps={creatorFragmentCommonProps}
              />
              <CreatorFragment
                config={{
                  id: "switch-creator-fragment",
                  props: {
                    field: "priority",
                    label: t("priorityString"),
                    icon: "notice-circle",
                  },
                }}
                commonProps={creatorFragmentCommonProps}
              />
              <CreateTaskSchedule
                disabled={blockInput}
                futureStart={futureStart}
                onChange={handleScheduleChange}
                // Ignore these for now
                repeatingStart=""
                end=""
                recurrence={null}
              />
            </FixaListView>
          </Section>
        </div>
      );
    }
  }, [
    loadingFailed,
    t,
    taskType,
    creatorFragmentCommonProps,
    blockInput,
    futureStart,
    handleScheduleChange,
  ]);

  return (
    <FixaModal visible={!!taskId} handleCloseBtn={handleCloseClick}>
      <FixaSheets
        fullSize
        className={loadingFailed ? "" : styles["greyBackground"]}
        header={header}
        footer={footer}
      >
        {content}
      </FixaSheets>
    </FixaModal>
  );
}

export default TaskEditorSheet;
